import React, { useMemo, useRef, useState } from "react";

/* ------------------------------------------------------------------ *
 * Plain Text / HTML code editor (same idea as SendX's HTML editor):
 *   - left  : code editor (write or paste raw HTML)
 *   - right : live preview inside a sandboxed iframe
 *   - device toggle (Desktop / Mobile)
 *   - import an .html file
 *   - insert merge tags at the cursor
 *   - name + category, then Save Template (reusable)
 *
 * No literal double-brace tokens are written in this file (merge tags
 * are built with mt() so raw "toolu_01F7KwEL4Zgfaq5yRnVpRkdB" never appears in source).
 * ------------------------------------------------------------------ */

const mt = (n) => "{" + "{" + n + "}" + "}";

const STARTER =
  '<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1f2330">\n' +
  '  <h1 style="color:#5d5fef">Hello ' + mt("first_name") + '</h1>\n' +
  '  <p>Write or paste your own HTML here. The preview on the right updates as you type.</p>\n' +
  '  <p><a href="https://example.com" style="display:inline-block;background:#5d5fef;color:#fff;padding:10px 18px;border-radius:6px;text-decoration:none">Call to action</a></p>\n' +
  '  <hr style="border:none;border-top:1px solid #e5e7f0;margin:24px 0"/>\n' +
  '  <p style="font-size:12px;color:#8a90a6">You are receiving this because you subscribed. <a href="#">Unsubscribe</a></p>\n' +
  '</div>';

const MERGE_TAGS = [
  { label: "First name", name: "first_name" },
  { label: "Last name", name: "last_name" },
  { label: "Email", name: "email" }
];

export default function HtmlEmailEditor({ initial, onSave, onClose }) {
  const [name, setName] = useState((initial && initial.title) || "");
  const [category, setCategory] = useState((initial && initial.category) || "Custom");
  const [html, setHtml] = useState(
    initial && typeof initial.html === "string" && initial.html.trim()
      ? initial.html
      : STARTER
  );
  const [device, setDevice] = useState("desktop");
  const codeRef = useRef(null);

  const categories = [
    "Custom", "Lifecycle", "SaaS", "Agency", "Inspiration",
    "Global Observances", "Dark Mode", "Photography", "Wedding"
  ];

  const previewDoc = useMemo(
    () =>
      '<!doctype html><html><head><meta charset="utf-8"/>' +
      '<meta name="viewport" content="width=device-width, initial-scale=1"/>' +
      '<style>body{margin:0;padding:16px;background:#ffffff;}</style></head><body>' +
      (html || "") +
      "</body></html>",
    [html]
  );

  function insertAtCursor(text) {
    const el = codeRef.current;
    if (!el) {
      setHtml((h) => h + text);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = html.slice(0, start) + text + html.slice(end);
    setHtml(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      try { el.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
    });
  }

  function handleImport(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setHtml(String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  }

  function handleTab(e) {
    if (e.key !== "Tab") return;
    e.preventDefault();
    insertAtCursor("  ");
  }

  function handleSave() {
    onSave({
      title: name.trim() || "Untitled template",
      category: category || "Custom",
      html: html || ""
    });
  }

  return (
    <div className="dee-overlay">
      <div className="dee-shell">
        <div className="dee-topbar">
          <div className="dee-title">
            <strong>MailX HTML</strong>
            <input
              className="dee-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Template name"
            />
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="dee-actions">
            <button type="button" className="dee-ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="dee-primary" onClick={handleSave}>
              Save Template
            </button>
          </div>
        </div>

        <div className="hee-toolbar">
          <div className="hee-tools-left">
            <label className="hee-import">
              Import HTML
              <input type="file" accept=".html,.htm,text/html" onChange={handleImport} />
            </label>
            <span className="hee-sep" />
            <span className="hee-tag-label">Insert:</span>
            {MERGE_TAGS.map((tag) => (
              <button
                key={tag.name}
                type="button"
                className="hee-tag"
                onClick={() => insertAtCursor(mt(tag.name))}
              >
                {tag.label}
              </button>
            ))}
          </div>
          <div className="hee-devices">
            <button
              type="button"
              className={device === "desktop" ? "active" : ""}
              onClick={() => setDevice("desktop")}
            >
              Desktop
            </button>
            <button
              type="button"
              className={device === "mobile" ? "active" : ""}
              onClick={() => setDevice("mobile")}
            >
              Mobile
            </button>
          </div>
        </div>

        <div className="hee-body">
          <div className="hee-code">
            <div className="hee-code-head">HTML</div>
            <textarea
              ref={codeRef}
              value={html}
              spellCheck={false}
              onChange={(e) => setHtml(e.target.value)}
              onKeyDown={handleTab}
              placeholder="Write or paste your HTML here..."
            />
          </div>
          <div className="hee-preview-pane">
            <div className="hee-code-head">Live preview</div>
            <div className="hee-preview-scroll">
              <iframe
                title="HTML preview"
                className={`hee-frame ${device}`}
                sandbox="allow-same-origin"
                srcDoc={previewDoc}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
